import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/StatusPill";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import InstallHelpModal from "@/components/InstallHelpModal";
import FirstLoginTour from "@/components/FirstLoginTour";
import NoWorkModal from "@/components/NoWorkModal";
import { weekRangeLabel } from "@/lib/weeks";
import { SignOut, Plus, Key, DownloadSimple, CalendarX } from "@phosphor-icons/react";

export default function ContractorHome() {
  const { user, logout } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [ts, setTs] = useState([]);
  const [showPw, setShowPw] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [showNoWork, setShowNoWork] = useState(false);
  const loadAssignments = () => api.get("/assignments").then((r) => setAssignments(Array.isArray(r.data) ? r.data : []));
  useEffect(() => {
    loadAssignments();
    api.get("/timesheets").then((r) => setTs(Array.isArray(r.data) ? r.data : []));
  }, []);
  const doLogout = () => logout();
  const nudgeWeek = assignments[0]?._nudge_week;
  const noWorkCount = assignments.filter((a) => a.no_work_this_week).length;

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white">
      <FirstLoginTour />
      <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-900 text-white">
        <div className="flex items-center gap-3">
          <img src={`${API_BASE}/settings/company/logo`} alt="SOS" className="h-10 w-10 rounded object-contain bg-white/10 p-0.5" />
          <div>
            <div className="font-bold text-sm leading-tight">Safety Onsite</div>
            <div className="text-xs text-slate-300">{user.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="contractor-change-pw" onClick={() => setShowPw(true)} className="p-2 text-slate-300 hover:text-white"><Key size={20} /></button>
          <button data-testid="contractor-logout" onClick={doLogout} className="p-2 text-slate-300 hover:text-white"><SignOut size={20} /></button>
        </div>
      </header>
      <div className="p-5">
        <div className="tinylabel">Active assignments</div>
        {assignments.length === 0 && <div className="text-slate-400 mt-2">No active assignments.</div>}
        <div className="mt-2 space-y-2">
          {assignments.map((a) => (
            <div key={a.id} className="border border-slate-200 rounded-md p-4" data-testid={`contractor-assignment-${a.id}`}>
              <div className="font-bold">{a._project_name || "Assignment"}</div>
              {a._client_name && <div className="text-sm text-slate-600 mt-0.5">{a._client_name}</div>}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                {a._project_location && <span>{a._project_location}</span>}
                {a._project_po && <span>PO: {a._project_po}</span>}
              </div>
              <div className="text-sm text-slate-500 mt-2">Pay ${a.pay_rate}/hr · Per diem ${a.per_diem_rate}/day</div>
              {a.no_work_this_week && (
                <span data-testid={`contractor-no-work-badge-${a.id}`} className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                  No work · {weekRangeLabel(a._nudge_week)}
                </span>
              )}
            </div>
          ))}
        </div>
        <Link data-testid="new-timesheet-btn" to="/contractor/new" className="mt-6 w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg font-bold rounded-lg flex items-center justify-center gap-2 transition-transform active:scale-95">
          <Plus size={22} weight="bold" /> New timesheet
        </Link>

        {assignments.length > 0 && (
          <button
            data-testid="contractor-no-work-btn"
            onClick={() => setShowNoWork(true)}
            className={`mt-3 w-full h-12 border font-semibold rounded-md flex items-center justify-center gap-2 ${noWorkCount ? "border-blue-300 bg-blue-50 text-blue-800" : "border-blue-300 text-blue-700 hover:bg-blue-50"}`}
          >
            <CalendarX size={18} weight="bold" />
            {noWorkCount ? `No work reported · ${weekRangeLabel(nudgeWeek)} (edit)` : `I didn't work last week (${weekRangeLabel(nudgeWeek)})`}
          </button>
        )}

        <button
          data-testid="install-help-btn"
          onClick={() => setShowInstall(true)}
          className="mt-3 w-full h-11 border border-slate-300 text-slate-700 font-semibold rounded-md flex items-center justify-center gap-2 hover:bg-slate-50"
        >
          <DownloadSimple size={18} weight="bold" /> Install as app on your phone
        </button>

        <div className="tinylabel mt-8 mb-2">My timesheets</div>
        {ts.length === 0 && <div className="text-slate-400 text-sm">Nothing submitted yet.</div>}
        <div className="space-y-2">
          {ts.map((t) => (
            <div key={t.id} className="border border-slate-200 rounded-md p-4" data-testid={`contractor-ts-${t.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold">Week of {t.week_start}</div>
                <StatusPill status={t.status} />
              </div>
              <div className="text-sm text-slate-500 mt-1">{t.total_hours} hrs · {t.per_diem_days} per-diem</div>
              {t.reject_reason && <div className="text-sm text-red-600 mt-1">Reason: {t.reject_reason}</div>}
              {t.status === "rejected" && (
                <Link to={`/contractor/edit/${t.id}`} className="mt-2 inline-block text-green-600 font-semibold">Fix & resubmit →</Link>
              )}
            </div>
          ))}
        </div>
      </div>
      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
      {showInstall && <InstallHelpModal onClose={() => setShowInstall(false)} />}
      {showNoWork && <NoWorkModal assignments={assignments} onClose={() => setShowNoWork(false)} onSaved={() => { setShowNoWork(false); loadAssignments(); }} />}
    </div>
  );
}
